import { useEffect, useMemo, useState } from 'react'
import type { ComponentProps, FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Card, Chip, ConfirmDeleteModal, EmptyState, Input, Modal, Skeleton, providerTag } from '../components/ui'
import { useCredentials, useEvents, useGroupsTree, useLocationTree, useMergePerson, usePerson, usePersonDuplicateGroup, usePersonEffectiveAccess, usePersons } from '../api/hooks'
import { api, ApiError } from '../api/client'
import type { CommandLog, DuplicateGroup, PersonOccupancy, PersonSyncState } from '../api/types'
import { useAuthStore } from '../stores/auth'
import { initials, relativeTime } from '../lib/format'
import { locationNames, locationPaths } from '../lib/locations'
import { readOnlyReason } from '../lib/dryrun'
import { groupPaths } from './Groups'
import { PersonFormModal, SelectField, personStatusTone, validityWindow } from './Directory'
import {
  CommandResultView,
  CredentialFormModal,
  credentialStatusTone,
  kindLabel,
  lastUsedSummary,
  lastUsedTitle,
} from './Credentials'
import { LocationLink } from '../components/LocationLink'
import { MergeModal } from '../components/features/MergeModal'
import { mergeWizardHref, useCanOperate } from './PersonDuplicates'

const OCCUPANCY_KINDS = [
  'resident',
  'owner',
  'board',
  'tenant',
  'worker',
  'contractor',
  'caretaker',
  'cleaner',
  'delivery',
  'guest',
  'other',
] as const

/** Chip tone per membership source — direct assignments stand out in gold. */
function sourceTone(source: 'rule' | 'group' | 'direct'): ComponentProps<typeof Chip>['tone'] {
  return source === 'direct' ? 'gold' : source === 'group' ? 'info' : 'muted'
}

function SyncBox({ state }: { state: PersonSyncState }) {
  const label = state.state === 'not_applicable' ? 'N/A' : state.state
  const color =
    state.state === 'synced'
      ? 'text-ostrel-ok'
      : state.state === 'pending'
        ? 'text-ostrel-warn'
        : 'text-ostrel-ink-muted'
  return (
    <span className="inline-flex flex-col items-center rounded-sm border border-ostrel-line bg-ostrel-sand px-2.5 py-1">
      <span className="tnum font-mono text-[9px] uppercase tracking-wider text-ostrel-ink-muted">
        {providerTag(state.provider)}
      </span>
      <span className={`tnum text-[9px] font-semibold uppercase tracking-wider ${color}`}>{label}</span>
    </span>
  )
}

/** Add an occupancy for a person, or for a directory group (every member
 *  then gets a derived occupancy at the location). */
export function AddOccupancyModal({
  open,
  onClose,
  target,
}: {
  open: boolean
  onClose: () => void
  target: { person: string } | { group: string }
}) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()
  const tree = useLocationTree()

  const [location, setLocation] = useState('')
  const [kind, setKind] = useState<string>('resident')
  const [validFrom, setValidFrom] = useState('')
  const [validTo, setValidTo] = useState('')

  const paths = tree.data ? locationPaths(tree.data) : new Map<string, string>()
  const locationOptions = Array.from(paths.entries()).map(([uid, path]) => ({ value: uid, label: path }))

  useEffect(() => {
    if (!open) return
    setLocation(locationOptions[0]?.value ?? '')
    setKind('resident')
    setValidFrom('')
    setValidTo('')
    // locationOptions is stable enough for a reset-on-open; only re-run when the modal opens.
  }, [open])

  const add = useMutation({
    mutationFn: async (): Promise<unknown> => {
      const body = { location, kind, valid_from: validFrom || null, valid_to: validTo || null }
      return 'person' in target
        ? api.createOccupancy({ person: target.person, ...body })
        : api.createGroupOccupancy(target.group, body)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope] })
      onClose()
    },
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    add.mutate()
  }

  return (
    <Modal open={open} title="Add occupancy" onClose={onClose}>
      {tree.isPending ? (
        <Skeleton className="h-32" />
      ) : locationOptions.length === 0 ? (
        <EmptyState title="No locations" hint="Add locations before assigning occupancies." />
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-3">
          <SelectField label="Location" value={location} onChange={setLocation} options={locationOptions} />
          <SelectField
            label="Kind"
            value={kind}
            onChange={setKind}
            options={OCCUPANCY_KINDS.map((k) => ({ value: k, label: k }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Valid from" type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
            <Input label="Valid to" type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
          </div>
          {add.isError && <p className="text-xs text-ostrel-danger">{add.error.message}</p>}
          <div className="mt-1 flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={add.isPending || !location}>
              {add.isPending ? 'Adding…' : 'Add occupancy'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  )
}

/** Display names for a vendor-derived occupancy's "via" chip. */
const SOURCE_NAMES: Record<string, string> = { came: 'CAME', techfass: 'TechFASS', axema: 'Axema', acre: 'Acre' }

export default function PersonDetailPage() {
  const { uid = '' } = useParams()
  const navigate = useNavigate()
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()

  const person = usePerson(uid)
  const tree = useLocationTree()
  const credentials = useCredentials()
  const groupsTree = useGroupsTree()
  const groupPathMap = useMemo(() => groupPaths(groupsTree.data ?? []), [groupsTree.data])
  const effective = usePersonEffectiveAccess(uid)
  const duplicates = usePersonDuplicateGroup(uid)
  const canOperate = useCanOperate()
  const [since7d] = useState(() => new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString())
  const events = useEvents({ person: uid, since: since7d })

  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [mergeOpen, setMergeOpen] = useState(false)
  const [issueOpen, setIssueOpen] = useState(false)
  const [occOpen, setOccOpen] = useState(false)
  const [removeOcc, setRemoveOcc] = useState<PersonOccupancy | null>(null)
  const [lastCommand, setLastCommand] = useState<CommandLog | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [scope] })

  const removeOccupancy = useMutation({
    mutationFn: api.deleteOccupancy,
    onSuccess: () => {
      setRemoveOcc(null)
      invalidate()
    },
  })
  const block = useMutation({
    mutationFn: api.blockCredential,
    onSuccess: (cmd) => {
      setLastCommand(cmd)
      invalidate()
    },
  })
  const unblock = useMutation({
    mutationFn: api.unblockCredential,
    onSuccess: (cmd) => {
      setLastCommand(cmd)
      invalidate()
    },
  })
  const reportLost = useMutation({
    // PATCH, not a status-only PUT (PUT is full-replace and 422s without kind + code).
    mutationFn: (credUid: string) => api.patchCredential(credUid, { status: 'lost' }),
    onSuccess: (cred) => {
      setLastCommand(cred.command)
      invalidate()
    },
  })
  const deletePerson = useMutation({
    mutationFn: () => api.deletePerson(uid),
    onSuccess: () => {
      invalidate()
      navigate('/directory')
    },
  })
  const mergePerson = useMergePerson()
  const persons = usePersons()
  const personOptions = (persons.data ?? [])
    .filter((p) => p.unique_id !== uid)
    .map((p) => ({ value: p.unique_id, label: `${p.display_name} · ${p.unique_id}` }))
  const mutating =
    removeOccupancy.isPending || block.isPending || unblock.isPending || reportLost.isPending || mergePerson.isPending

  const paths = tree.data ? locationPaths(tree.data) : new Map<string, string>()
  const locNames = tree.data ? locationNames(tree.data) : new Map<string, string>()
  const effectiveGroupNames = new Map((effective.data?.groups ?? []).map((g) => [g.unique_id, g.name]))
  // Least-recently-used first (never-used first of all) — a quiet tag
  // (lost, unused) stands out at the top instead of hiding at the bottom.
  const personCreds = (credentials.data ?? [])
    .filter((c) => c.person === uid)
    .sort((a, b) => {
      if (!a.last_used_at && !b.last_used_at) return 0
      if (!a.last_used_at) return -1
      if (!b.last_used_at) return 1
      return new Date(a.last_used_at).getTime() - new Date(b.last_used_at).getTime()
    })
  const activity = (events.data?.items ?? []).filter((e) => e.person === uid).slice(0, 8)

  if (person.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-36" />
        <div className="grid gap-4 lg:grid-cols-3">
          <Skeleton className="h-56 lg:col-span-2" />
          <Skeleton className="h-56" />
        </div>
      </div>
    )
  }

  if (person.isError) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink uid={uid} />
        <EmptyState
          title="Could not load person"
          hint={person.error.message}
          action={
            <Link to="/directory" className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold hover:text-ostrel-accent">
              ← Back to people
            </Link>
          }
        />
      </div>
    )
  }

  const p = person.data
  const validity = validityWindow(p.valid_from, p.valid_to)
  // A person bound to a dry-run system is read-only (edit/delete/issue).
  const personReadOnly = readOnlyReason(p)
  const visibleSyncStates = p.sync_states.filter((s) => s.state !== 'not_applicable')
  const duplicateGroup = duplicates.data?.group ?? null

  return (
    <div className="flex flex-col gap-4">
      <BackLink uid={uid} />

      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-sm bg-ostrel-navy text-sm font-semibold text-ostrel-gold">
              {initials(p.display_name || '?')}
            </span>
            <div>
              <h1 className="font-display text-2xl text-ostrel-ink">{p.display_name || 'Unnamed'}</h1>
              <p className="tnum mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-ostrel-ink-muted">
                <span className="font-mono">{p.unique_id}</span>
                <Chip tone={personStatusTone(p.status)} label={p.status} />
                {/* Visibility flags surface only when OFF — both default on,
                    so a chip here always means "this person is restricted". */}
                {!p.show_on_intercom && <Chip tone="muted" label="hidden from intercom" />}
                {!p.mailbox_access && <Chip tone="muted" label="no mailbox access" />}
                {p.emails[0] && <span>{p.emails[0]}</span>}
                {p.phones[0] && <span>{p.phones[0]}</span>}
                {validity && <span>{validity}</span>}
              </p>
              {p.note && <p className="mt-1.5 text-xs text-ostrel-ink-muted">{p.note}</p>}
              {duplicateGroup && <DuplicatesHint uid={uid} group={duplicateGroup} canOperate={canOperate} />}
            </div>
          </div>
          {visibleSyncStates.length > 0 && (
            <div className="flex gap-1.5">
              {visibleSyncStates.map((s) => (
                <SyncBox key={s.provider} state={s} />
              ))}
            </div>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            variant="navy"
            size="sm"
            disabled={personReadOnly !== undefined}
            title={personReadOnly}
            onClick={() => setIssueOpen(true)}
          >
            Issue credential
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={personReadOnly !== undefined}
            title={personReadOnly}
            onClick={() => setEditOpen(true)}
          >
            Edit person
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={personReadOnly !== undefined}
            title={personReadOnly}
            onClick={() => setMergeOpen(true)}
          >
            Merge…
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={personReadOnly !== undefined}
            title={personReadOnly}
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </Button>
        </div>
      </Card>

      {lastCommand && <CommandResultView command={lastCommand} />}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <Card
            title="Occupancies"
            action={
              <Button variant="ghost" size="sm" onClick={() => setOccOpen(true)}>
                + Add
              </Button>
            }
          >
            {p.occupancies.length === 0 ? (
              <EmptyState title="No occupancies" hint="Link this person to an apartment, garage, or other location." />
            ) : (
              <ul className="flex flex-col divide-y divide-ostrel-line">
                {p.occupancies.map((o) => (
                  <li key={o.unique_id} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-ostrel-ink">
                        <LocationLink uid={o.location}>
                          {paths.get(o.location) ?? locNames.get(o.location) ?? o.location}
                        </LocationLink>
                      </span>
                      <span className="tnum block text-[10px] text-ostrel-ink-muted">{o.unique_id}</span>
                    </span>
                    {o.source_system && (
                      <Link to={`/systems/${o.source_system}`} title="Derived from the source system's data — it follows that system">
                        <Chip tone="info" label={`via ${SOURCE_NAMES[o.source_provider ?? ''] ?? o.source_provider ?? 'system'}`} />
                      </Link>
                    )}
                    {o.via_group && (
                      <Link to={`/groups/${o.via_group}`} title="Derived from the group's occupancy — manage it on the group">
                        <Chip tone="info" label={`via ${groupPathMap.get(o.via_group) ?? o.via_group}`} />
                      </Link>
                    )}
                    <Chip tone={o.kind === 'resident' ? 'gold' : 'muted'} label={o.kind} />
                    {!o.group_occupancy && !o.source_system && (
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={mutating}
                        onClick={() => setRemoveOcc(o)}
                      >
                        Remove
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {removeOccupancy.isError && (
              <p className="mt-2 text-xs text-ostrel-danger">{removeOccupancy.error.message}</p>
            )}
          </Card>

          <Card
            title="Credentials"
            action={
              <span className="tnum text-[11px] text-ostrel-ink-muted">
                {personCreds.length} {personCreds.length === 1 ? 'credential' : 'credentials'}
              </span>
            }
          >
            {credentials.isPending ? (
              <div className="flex flex-col gap-2">
                <Skeleton className="h-9" />
                <Skeleton className="h-9" />
              </div>
            ) : personCreds.length === 0 ? (
              <EmptyState
                title="No credentials"
                hint="Issue a card, fob, or PIN to this person."
                action={
                  <Button variant="navy" size="sm" onClick={() => setIssueOpen(true)}>
                    Issue credential
                  </Button>
                }
              />
            ) : (
              <ul className="flex flex-col divide-y divide-ostrel-line">
                {personCreds.map((c) => {
                  return (
                    <li key={c.unique_id} className="flex flex-wrap items-center gap-2 py-2.5 first:pt-0 last:pb-0">
                      <Chip tone="muted" label={kindLabel(c.kind)} />
                      <span className="min-w-0">
                        <Link
                          to={`/credentials/${c.unique_id}`}
                          className="tnum block font-mono text-sm text-ostrel-ink hover:text-ostrel-accent"
                        >
                          {c.code}
                        </Link>
                        <span className="tnum block text-[10px] text-ostrel-ink-muted" title={lastUsedTitle(c)}>
                          {lastUsedSummary(c)}
                        </span>
                      </span>
                      <span className="ml-auto flex items-center gap-2">
                        <Chip tone={credentialStatusTone(c.status)} label={c.status} />
                        {c.status === 'blocked' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={mutating || personReadOnly !== undefined}
                            title={personReadOnly}
                            onClick={() => unblock.mutate(c.unique_id)}
                          >
                            Unblock
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={mutating || personReadOnly !== undefined}
                            title={personReadOnly}
                            onClick={() => block.mutate(c.unique_id)}
                          >
                            Block
                          </Button>
                        )}
                        {c.status === 'active' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={mutating || personReadOnly !== undefined}
                            title={personReadOnly}
                            onClick={() => reportLost.mutate(c.unique_id)}
                          >
                            Lost
                          </Button>
                        )}
                      </span>
                    </li>
                  )
                })}
              </ul>
            )}
            {(block.isError || unblock.isError || reportLost.isError) && (
              <p className="mt-2 text-xs text-ostrel-danger">
                {(block.error ?? unblock.error ?? reportLost.error)?.message}
              </p>
            )}
          </Card>
        </div>

        <div className="flex flex-col gap-4 self-start">
          <Card
            title="Groups"
            action={
              <Button
                variant="ghost"
                size="sm"
                disabled={personReadOnly !== undefined}
                title={personReadOnly}
                onClick={() => navigate(`/persons/${uid}/membership`)}
              >
                Edit membership
              </Button>
            }
          >
            {/* Read-only: the groups the person IS in, with full tree paths.
                Membership editing is SENSITIVE — it lives on the dedicated
                /persons/{uid}/membership page, not inline. */}
            {groupsTree.isPending ? (
              <Skeleton className="h-9" />
            ) : p.groups.length === 0 ? (
              <p className="text-xs text-ostrel-ink-muted">
                Not a member of any group — Edit membership to add one.
              </p>
            ) : (
              <ul className="flex flex-col gap-1">
                {p.groups.map((g) => (
                  <li key={g} className="flex items-center gap-2">
                    <Link
                      to={`/groups/${g}`}
                      className="min-w-0 flex-1 truncate text-sm text-ostrel-ink hover:text-ostrel-accent"
                    >
                      {groupPathMap.get(g) ?? g}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Effective access">
            {effective.isPending ? (
              <div className="flex flex-col gap-2">
                <Skeleton className="h-9" />
                <Skeleton className="h-9" />
              </div>
            ) : effective.isError ? (
              <EmptyState title="Could not load effective access" hint={effective.error.message} />
            ) : effective.data.groups.length === 0 && effective.data.doors.length === 0 ? (
              <EmptyState
                title="No effective access"
                hint="Group memberships and occupancies resolve to no door access for this person."
              />
            ) : (
              <div className="flex flex-col gap-4">
                {effective.data.groups.length > 0 && (
                  <div>
                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                      Access groups
                    </p>
                    <ul className="flex flex-col divide-y divide-ostrel-line">
                      {effective.data.groups.map((g) => (
                        <li key={g.unique_id} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
                          <Link
                            to={`/access-groups/${g.unique_id}`}
                            className="min-w-0 flex-1 truncate text-sm font-medium text-ostrel-ink hover:text-ostrel-accent"
                          >
                            {g.name}
                          </Link>
                          <Chip tone={sourceTone(g.source)} label={g.source} />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {effective.data.doors.length > 0 && (
                  <div>
                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                      Doors
                    </p>
                    <ul className="flex flex-col divide-y divide-ostrel-line">
                      {effective.data.doors.map((d) => (
                        <li key={d.door} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-ostrel-ink">{d.name}</span>
                            <span className="block truncate text-[11px] text-ostrel-ink-muted">{d.path.join(' › ')}</span>
                            {d.decided_by ? (
                              <span className="block truncate text-[11px] text-ostrel-ink-muted">
                                via {effectiveGroupNames.get(d.decided_by) ?? d.decided_by}
                              </span>
                            ) : (
                              d.via.length > 0 && (
                                <span className="block truncate text-[11px] text-ostrel-ink-muted">
                                  via {d.via.map((v) => effectiveGroupNames.get(v.access_group) ?? v.access_group).join(', ')}
                                </span>
                              )
                            )}
                          </span>
                          <span className="tnum shrink-0 text-[10px] text-ostrel-ink-muted">
                            {d.schedule ?? 'always'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </Card>

          <Card title="Recent activity · 7 days">
          {events.isPending ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-9" />
              <Skeleton className="h-9" />
              <Skeleton className="h-9" />
            </div>
          ) : events.isError ? (
            <EmptyState title="Could not load activity" hint={events.error.message} />
          ) : activity.length === 0 ? (
            <EmptyState title="No recent activity" hint="Events from the last 7 days involving this person appear here." />
          ) : (
            <ul className="flex flex-col divide-y divide-ostrel-line">
              {activity.map((e) => (
                <li key={e.unique_id} className="flex items-start gap-3 py-2 first:pt-0 last:pb-0">
                  <span className="tnum w-16 shrink-0 pt-0.5 text-[10px] text-ostrel-ink-muted">
                    {relativeTime(e.occurred_at)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-ostrel-ink">
                      {e.type.replace(/_/g, ' ')}
                    </span>
                    <span className="tnum block truncate text-[11px] text-ostrel-ink-muted">
                      {e.location ? (
                        <LocationLink uid={e.location}>{locNames.get(e.location) ?? e.location}</LocationLink>
                      ) : (
                        (e.device ?? e.unique_id)
                      )}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          </Card>
        </div>
      </div>

      <PersonFormModal open={editOpen} onClose={() => setEditOpen(false)} person={p} />
      <CredentialFormModal open={issueOpen} onClose={() => setIssueOpen(false)} person={uid} />
      <AddOccupancyModal open={occOpen} onClose={() => setOccOpen(false)} target={{ person: uid }} />
      <MergeModal
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        title="Merge person"
        description="Pick another person to merge into. The current person is deleted and all of its data (credentials, occupancies, group memberships) moves to the target."
        source={{
          unique_id: p.unique_id,
          label: p.display_name || 'Unnamed',
          kind: 'person',
        }}
        options={personOptions}
        loading={persons.isPending}
        pending={mergePerson.isPending}
        error={mergePerson.error}
        errorAction={
          // 409: a vendor conflict, or the plain merge would change the
          // target's doors -- the guided merge decides access explicitly.
          mergePerson.error instanceof ApiError && mergePerson.error.status === 409 && mergePerson.variables ? (
            <p className="text-xs text-ostrel-ink">
              This merge needs an explicit access decision.{' '}
              <Link
                to={mergeWizardHref([uid, mergePerson.variables.target])}
                className="font-semibold text-ostrel-gold hover:text-ostrel-accent"
              >
                Open the guided merge →
              </Link>
            </p>
          ) : undefined
        }
        onConfirm={(target) =>
          mergePerson.mutate(
            { uid, target },
            {
              onSuccess: () => {
                setMergeOpen(false)
                navigate(`/directory/${target}`)
              },
            },
          )
        }
      />

      <ConfirmDeleteModal
        open={deleteOpen}
        title="Delete person"
        objectName={p.display_name || p.unique_id}
        expected={p.unique_id}
        pending={deletePerson.isPending}
        error={deletePerson.isError ? deletePerson.error.message : null}
        confirmLabel={deletePerson.isPending ? 'Deleting…' : 'Delete person'}
        onConfirm={() => deletePerson.mutate()}
        onClose={() => setDeleteOpen(false)}
      >
        <div className="flex flex-col gap-3">
          <p>
            Delete <span className="font-medium">{p.display_name || p.unique_id}</span> ({p.unique_id})?
            This cannot be undone.
          </p>
          {personCreds.length > 0 && (
            <p className="text-xs text-ostrel-warn">
              {personCreds.length} credential{personCreds.length === 1 ? ' is' : 's are'} assigned to this
              person.
            </p>
          )}
        </div>
      </ConfirmDeleteModal>

      <ConfirmDeleteModal
        open={removeOcc !== null}
        title="Remove occupancy"
        objectName={removeOcc ? (paths.get(removeOcc.location) ?? removeOcc.location) : ''}
        expected={removeOcc?.unique_id ?? ''}
        pending={removeOccupancy.isPending}
        error={removeOccupancy.isError ? removeOccupancy.error.message : null}
        confirmLabel={removeOccupancy.isPending ? 'Removing…' : 'Remove'}
        onConfirm={() => removeOcc && removeOccupancy.mutate(removeOcc.unique_id)}
        onClose={() => setRemoveOcc(null)}
      >
        <p>
          Remove the occupancy at{' '}
          <span className="font-medium">
            {removeOcc ? (paths.get(removeOcc.location) ?? removeOcc.location) : ''}
          </span>
          ? Group membership and door access derived from it are revoked.
        </p>
      </ConfirmDeleteModal>
    </div>
  )
}

/** "Possible duplicates (N)": other people at the same place under the same
 *  name. Operators get a link into the guided merge wizard. */
function DuplicatesHint({ uid, group, canOperate }: { uid: string; group: DuplicateGroup; canOperate: boolean }) {
  const others = group.persons.filter((d) => d.unique_id !== uid)
  if (others.length === 0) return null
  const label = `Possible duplicates (${others.length})`
  const names = others.map((d) => d.display_name || d.unique_id).join(', ')
  return (
    <p className="mt-2 flex flex-wrap items-center gap-2 text-[11px]" data-testid="duplicates-hint">
      <span className="inline-block h-2 w-2 rotate-45 bg-ostrel-warn" />
      {canOperate ? (
        <Link
          to={mergeWizardHref(group.persons.map((d) => d.unique_id))}
          className="font-semibold text-ostrel-warn hover:text-ostrel-accent"
          title={names}
        >
          {label} →
        </Link>
      ) : (
        <span className="font-semibold text-ostrel-warn" title={names}>
          {label}
        </span>
      )}
      <span className="text-ostrel-ink-muted">{names}</span>
    </p>
  )
}

function BackLink({ uid }: { uid: string }) {
  return (
    <p className="tnum flex items-center gap-2 text-[11px] text-ostrel-ink-muted">
      <Link to="/directory" className="hover:text-ostrel-ink">
        ← People
      </Link>
      <span>/</span>
      <span className="font-mono">{uid}</span>
    </p>
  )
}
